/**
 * AdminProjectDetailPage — one project, in full, for the admin surface.
 *
 * Reads GET /api/admin/projects/:id (AdminDashboardService.getProjectDetail):
 * project fields, client, contractor, project manager, milestones, escrow and
 * activity counts.
 *
 * Actions: status change (including cancel), via the existing
 * PATCH /api/projects/:id/status, which already admits an admin caller. There
 * are no other admin-only project mutations in the backend, so nothing else is
 * offered here.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Avatar,
  Chip,
  Button,
  Alert,
  Skeleton,
  Divider,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Link,
} from '@mui/material';
import {
  ArrowBack,
  Person,
  Engineering,
  OpenInNew,
  SwapHoriz,
} from '@mui/icons-material';

import adminService from '../services/adminService';
import { panelSx, heroPanelSx, panelHeaderSx, tableSx } from '../components/admin/surfaces';
import { brand, projectStatus, status as statusTokens, text, border } from '../theme/colors';
import { formatCurrency } from '../utils/helpers';

const EMPTY = '—';

// projects_status_check
const STATUS_OPTIONS = ['planning', 'open_for_bids', 'in_progress', 'completed', 'cancelled'];

const titleise = (value) =>
  value == null || value === '' ? EMPTY : String(value).replace(/_/g, ' ');

const formatDate = (value, withTime = false) => {
  if (!value) return EMPTY;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return EMPTY;
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
};

const Field = ({ label, value, span = 6 }) => (
  <Grid item xs={12} sm={span}>
    <Typography
      variant="caption"
      sx={{ color: text.tertiary, fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase' }}
    >
      {label}
    </Typography>
    <Typography sx={{ color: text.primary, fontSize: 14, mt: 0.25, wordBreak: 'break-word' }}>
      {value}
    </Typography>
  </Grid>
);

const Section = ({ title, action, children, grid = true }) => (
  <Paper elevation={0} sx={{ ...panelSx, mb: 2.5 }}>
    <Box
      sx={{
        ...panelHeaderSx,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
      }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: 14, color: text.primary }}>{title}</Typography>
      {action}
    </Box>
    {grid ? (
      <Box sx={{ p: 2.5 }}>
        <Grid container spacing={2.5}>
          {children}
        </Grid>
      </Box>
    ) : (
      children
    )}
  </Paper>
);

/** Client / contractor / PM card. */
const PartyCard = ({ label, party, icon, onOpen }) => (
  <Grid item xs={12} md={4}>
    <Box sx={{ ...panelSx, p: 2, height: '100%', boxShadow: 'none' }}>
      <Typography
        variant="caption"
        sx={{ color: text.tertiary, fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase' }}
      >
        {label}
      </Typography>
      {!party ? (
        <Typography sx={{ color: text.tertiary, fontSize: 14, mt: 1 }}>Not assigned</Typography>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
          <Avatar
            src={party.imageUrl || undefined}
            sx={{
              width: 36,
              height: 36,
              fontSize: 13,
              fontWeight: 600,
              bgcolor: `${brand.primary}1a`,
              color: brand.primary,
            }}
          >
            {(party.name || party.email || '?').charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 500, color: text.primary }} noWrap>
              {party.name || party.email}
            </Typography>
            <Typography variant="caption" sx={{ color: text.tertiary, display: 'block' }} noWrap>
              {party.email || (party.manual ? 'Off-platform contractor' : EMPTY)}
            </Typography>
          </Box>
          {party.id && (
            <Button
              size="small"
              startIcon={icon}
              onClick={onOpen}
              sx={{ textTransform: 'none', minWidth: 0, whiteSpace: 'nowrap' }}
            >
              Open
            </Button>
          )}
        </Box>
      )}
    </Box>
  </Grid>
);

const AdminProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [nextStatus, setNextStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [notice, setNotice] = useState(null);

  const listSearch = location.state?.listSearch || '';
  const listPath = location.state?.listPath || '/admin/home/projects';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await adminService.getProjectById(projectId);
      setData(result);
      setError(null);
    } catch (err) {
      setError(
        err?.response?.status === 404
          ? 'That project does not exist.'
          : err?.response?.data?.error || err?.message || 'Could not load this project.'
      );
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const project = data?.project;
  const milestones = data?.milestones || [];
  const escrow = data?.escrow;
  const counts = data?.counts;
  const tone = projectStatus[project?.status] || statusTokens.neutral;

  const openStatusDialog = () => {
    setActionError(null);
    setNextStatus(project?.status || '');
    setDialogOpen(true);
  };

  const saveStatus = async () => {
    setBusy(true);
    setActionError(null);
    try {
      await adminService.setProjectStatus(projectId, nextStatus);
      setDialogOpen(false);
      setNotice(`Status changed to ${titleise(nextStatus)}.`);
      await load();
    } catch (err) {
      setActionError(
        err?.response?.data?.message
          || err?.response?.data?.error
          || err?.message
          || 'The status could not be changed.'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(`${listPath}${listSearch}`)}
        sx={{ textTransform: 'none', color: text.secondary, mb: 2 }}
      >
        Back to projects
      </Button>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={load}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {notice && (
        <Alert severity="success" sx={{ mb: 2.5, borderRadius: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      {loading ? (
        <>
          <Skeleton variant="rounded" height={140} sx={{ borderRadius: 2, mb: 2.5 }} />
          <Skeleton variant="rounded" height={180} sx={{ borderRadius: 2, mb: 2.5 }} />
          <Skeleton variant="rounded" height={200} sx={{ borderRadius: 2 }} />
        </>
      ) : !project ? null : (
        <>
          {/* Header */}
          <Paper elevation={0} sx={{ ...heroPanelSx, p: 2.5, mb: 2.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'flex-start', sm: 'center' },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: 22, fontWeight: 700, color: text.primary }}>
                  {project.title || '(untitled project)'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
                  <Chip
                    size="small"
                    label={titleise(project.status)}
                    sx={{
                      textTransform: 'capitalize',
                      fontWeight: 600,
                      fontSize: 12,
                      color: tone,
                      bgcolor: `${tone}1a`,
                    }}
                  />
                  <Typography variant="caption" sx={{ color: text.tertiary }}>
                    {project.location || 'No location'} · created {formatDate(project.createdAt)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  disableElevation
                  startIcon={<SwapHoriz />}
                  onClick={openStatusDialog}
                  sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                >
                  Change status
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<OpenInNew />}
                  // The route is /projects/:id (ProjectsRouter); /project/ 404s.
                  onClick={() => navigate(`/projects/${encodeURIComponent(project.id)}`)}
                  sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                >
                  Project view
                </Button>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2.5 }}>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, Math.max(0, project.completionPercentage))}
                sx={{
                  flex: 1,
                  height: 8,
                  borderRadius: 4,
                  bgcolor: border.light,
                  '& .MuiLinearProgress-bar': { bgcolor: tone, borderRadius: 4 },
                }}
              />
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: text.secondary, minWidth: 40 }}>
                {project.completionPercentage}%
              </Typography>
            </Box>
          </Paper>

          {/* Parties */}
          <Paper elevation={0} sx={{ ...panelSx, p: 2.5, mb: 2.5 }}>
            <Grid container spacing={2}>
              <PartyCard
                label="Client"
                party={data.owner}
                icon={<Person sx={{ fontSize: 16 }} />}
                onOpen={() =>
                  navigate(`/admin/home/users/${data.owner.id}`, {
                    state: { listSearch: location.search, listPath: location.pathname },
                  })
                }
              />
              <PartyCard
                label="Contractor"
                party={data.contractor}
                icon={<Engineering sx={{ fontSize: 16 }} />}
                onOpen={() =>
                  navigate(`/admin/home/users/${data.contractor.id}`, {
                    state: { listSearch: location.search, listPath: location.pathname },
                  })
                }
              />
              <PartyCard
                label="Project manager"
                party={data.projectManager}
                icon={<Person sx={{ fontSize: 16 }} />}
                onOpen={() =>
                  navigate(`/admin/home/users/${data.projectManager.id}`, {
                    state: { listSearch: location.search, listPath: location.pathname },
                  })
                }
              />
            </Grid>
          </Paper>

          {/* Activity counts */}
          {counts && (
            <Paper elevation={0} sx={{ ...panelSx, p: 2.5, mb: 2.5 }}>
              <Grid container spacing={2.5}>
                {[
                  ['Quotations', counts.bids],
                  ['Proposals', counts.proposals],
                  ['Verifications', counts.verifications],
                  ['Diary entries', counts.diaryEntries],
                ].map(([label, value]) => (
                  <Grid item xs={6} sm={3} key={label}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: text.tertiary,
                        fontWeight: 600,
                        letterSpacing: 0.3,
                        textTransform: 'uppercase',
                      }}
                    >
                      {label}
                    </Typography>
                    <Typography sx={{ fontSize: 22, fontWeight: 700, color: text.primary }}>
                      {value}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}

          {/* Escrow */}
          <Section title="Escrow">
            {!escrow ? (
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: text.tertiary }}>
                  No escrow account for this project.
                </Typography>
              </Grid>
            ) : (
              <>
                <Field label="Status" value={titleise(escrow.status)} />
                <Field
                  label="Total"
                  value={escrow.totalAmount != null
                    ? formatCurrency(escrow.totalAmount, escrow.currency || 'USD')
                    : EMPTY}
                />
                <Field
                  label="Platform fee"
                  value={escrow.platformFeeAmount != null
                    ? formatCurrency(escrow.platformFeeAmount, escrow.currency || 'USD')
                    : (escrow.platformFeePercentage != null
                      ? `${escrow.platformFeePercentage}% (not yet calculated)`
                      : EMPTY)}
                />
                <Field label="Funding source" value={titleise(escrow.fundingSource)} />
                <Field label="Funded" value={formatDate(escrow.fundedAt, true)} />
                <Field label="Completed" value={formatDate(escrow.completedAt, true)} />
              </>
            )}
          </Section>

          {/* Milestones */}
          <Section
            title={`Milestones (${milestones.length})`}
            grid={false}
          >
            {milestones.length === 0 ? (
              <Box sx={{ p: 2.5 }}>
                <Typography variant="body2" sx={{ color: text.tertiary }}>
                  No milestones on this project.
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small" sx={tableSx}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Milestone</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Verification</TableCell>
                      <TableCell>Budget</TableCell>
                      <TableCell>Progress</TableCell>
                      <TableCell>Planned</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {milestones.map((m) => (
                      <TableRow key={m.id}>
                        <TableCell sx={{ color: text.primary, fontWeight: 500 }}>
                          {m.name || EMPTY}
                          {m.type && (
                            <Typography variant="caption" sx={{ color: text.tertiary, display: 'block' }}>
                              {titleise(m.type)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ color: text.secondary, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
                          {titleise(m.status)}
                        </TableCell>
                        <TableCell sx={{ color: text.secondary, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
                          {titleise(m.verificationStatus)}
                        </TableCell>
                        <TableCell sx={{ color: text.secondary, whiteSpace: 'nowrap' }}>
                          {m.budgetAllocation ? formatCurrency(m.budgetAllocation) : EMPTY}
                        </TableCell>
                        <TableCell sx={{ color: text.secondary, whiteSpace: 'nowrap' }}>
                          {m.progress}%
                        </TableCell>
                        <TableCell sx={{ color: text.secondary, whiteSpace: 'nowrap' }}>
                          {m.plannedStart || m.plannedEnd
                            ? `${formatDate(m.plannedStart)} → ${formatDate(m.plannedEnd)}`
                            : EMPTY}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Section>

          {/* Project fields */}
          <Section title="Project details">
            <Field label="Project ID" value={project.id} span={12} />
            <Field label="Budget" value={project.budget ? formatCurrency(project.budget) : EMPTY} />
            <Field label="Type" value={titleise(project.fullProjectType || project.projectType)} />
            <Field label="Task type" value={titleise(project.taskType)} />
            <Field label="Open to quotations" value={project.isOpenToBids ? 'Yes' : 'No'} />
            <Field label="Quotation deadline" value={formatDate(project.proposalDeadline)} />
            <Field
              label="Min contractor rating"
              value={project.minContractorRating ?? EMPTY}
            />
            <Field label="PM assignment" value={titleise(project.pmAssignmentStatus)} />
            <Field label="Updated" value={formatDate(project.updatedAt, true)} />
            <Field
              label="Coordinates"
              value={project.latitude != null && project.longitude != null
                ? `${project.latitude}, ${project.longitude}`
                : EMPTY}
            />
            {project.requiredCertifications && (
              <Field label="Required certifications" value={project.requiredCertifications} span={12} />
            )}
            {project.masterContractUrl && (
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  sx={{ color: text.tertiary, fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase' }}
                >
                  Master contract
                </Typography>
                <Box>
                  <Link
                    href={project.masterContractUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{ fontSize: 14, color: brand.primary, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                  >
                    Open contract <OpenInNew sx={{ fontSize: 14 }} />
                  </Link>
                </Box>
              </Grid>
            )}
            {project.description && (
              <>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Field label="Description" value={project.description} span={12} />
              </>
            )}
          </Section>
        </>
      )}

      {/* Status change */}
      <Dialog open={dialogOpen} onClose={() => !busy && setDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Change project status</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Currently <strong>{titleise(project?.status)}</strong>. This notifies the client and
            contractor the same way their own status changes do.
          </DialogContentText>

          {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}

          <TextField
            select
            fullWidth
            label="New status"
            value={nextStatus}
            onChange={(e) => setNextStatus(e.target.value)}
          >
            {STATUS_OPTIONS.map((st) => (
              <MenuItem key={st} value={st} sx={{ textTransform: 'capitalize' }}>
                {titleise(st)}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={busy} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            disabled={busy || !nextStatus || nextStatus === project?.status}
            onClick={saveStatus}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <SwapHoriz />}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Save status
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminProjectDetailPage;
